import { useCallback, useEffect, useRef } from "react";
import { templatesApi } from "../api.js";
import { usePreviewStore } from "../stores/previewStore.js";
import type { BlockMapEntry, TemplateDef } from "@juro/shared";

const DEBOUNCE_MS = 400;

function parseBlockMap(header: string | null): BlockMapEntry[] {
  if (!header) return [];
  const raw = JSON.parse(header) as Record<string, [number, number, number, number]>;
  return Object.entries(raw).map(([id, bounds]) => ({ id, bounds }));
}

export function useLivePreview(template: TemplateDef | null): void {
  const setGenerating = usePreviewStore((state) => state.setGenerating);
  const setBlobUrl = usePreviewStore((state) => state.setBlobUrl);
  const setError = usePreviewStore((state) => state.setError);
  const setBlockMap = usePreviewStore((state) => state.setBlockMap);
  const clearPreview = usePreviewStore((state) => state.clearPreview);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const requestPreview = useCallback((draft: TemplateDef) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      setGenerating(true);
      setError(null);

      try {
        const res = await templatesApi.render(draft, abortRef.current.signal);
        setBlockMap(parseBlockMap(res.blockMapHeader));
        setBlobUrl(URL.createObjectURL(res.blob));
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setGenerating(false);
      }
    }, DEBOUNCE_MS);
  }, [setBlockMap, setBlobUrl, setError, setGenerating]);

  useEffect(() => {
    if (template) requestPreview(template);
  }, [template, requestPreview]);

  useEffect(() => () => {
    abortRef.current?.abort();
    if (timerRef.current) clearTimeout(timerRef.current);
    clearPreview();
  }, [clearPreview]);
}
